import { useQuery } from '@tanstack/react-query';
import { isSiteOrderActive } from '@cp/shared';
import { siteOrdersApi } from '@/api/siteOrders';
import { queryKeys } from '@/lib/queryKeys';
import { useApiMutation, type MutationCallbacks } from '@/hooks/useApiMutation';
import type { CreateSiteOrderRequest, SiteOrderView } from '@/types/api';

/** Makes the order; the domain is paid for in this request (ADR-0059). */
export function useCreateSiteOrder(callbacks?: MutationCallbacks<SiteOrderView, { input: CreateSiteOrderRequest; key: string }>) {
  return useApiMutation(
    ({ input, key }) => siteOrdersApi.create(input, key),
    () => [queryKeys.siteOrders.all, queryKeys.domainPurchases.all, queryKeys.sites.all],
    callbacks,
  );
}

/** One order, asked every two seconds while it is under way, not once it is over. */
export function useSiteOrder(id: string | null) {
  return useQuery({
    queryKey: queryKeys.siteOrders.one(id ?? ''),
    queryFn: () => siteOrdersApi.get(id!),
    enabled: id !== null,
    refetchInterval: (query) => (query.state.data && !isSiteOrderActive(query.state.data.state) ? false : 2_000),
  });
}

/** The strip of sites being made: every ten seconds while one is, every minute otherwise. */
export function useRecentSiteOrders({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.siteOrders.all,
    queryFn: siteOrdersApi.recent,
    enabled,
    refetchInterval: (query) => ((query.state.data ?? []).some((order) => isSiteOrderActive(order.state)) ? 10_000 : 60_000),
  });
}
